// src/ui/components/recentWorkDeck.ts — Tudor's Chronological Work Log & Expedition Archive
// Aggregates completed sets, scores, stars, Victorian seals, worksheet remediations, and mock exams
// across all learning stations for parent visibility and learner pride.

import { tokens } from '../designTokens';
import { soundEngine } from '../../audio/soundEngine';
import { StationProgression, type SetCompletionRecord, type MasterySealTier } from '../../storage/stationProgression';
import { ScannerStorage, type SchoolworkScanRecord } from '../../storage/scannerStorage';
import { MockExamStorage, type MockExamRecord } from '../../storage/mockExamStorage';
import { SpellingStorage } from '../../storage/spellingStorage';
import { renderCornerFiligree } from './ornateHelpers';

export type WorkLogCategory = 'all' | 'geometry' | 'comprehension' | 'spelling' | 'scanner' | 'mock';

export interface WorkLogEntry {
  id: string;
  timestamp: number;
  dateFormatted: string;
  category: WorkLogCategory;
  stationName: string;
  stationEmblem: string;
  title: string;
  subtitle: string;
  scoreText: string;
  percent: number;
  stars?: 1 | 2 | 3;
  seal?: MasterySealTier;
  earnedEp?: number;
  route: string;
}

export class RecentWorkDeck {
  private container: HTMLElement;
  private activeCategory: WorkLogCategory = 'all';

  constructor() {
    this.container = document.createElement('section');
    this.container.id = 'recent-work-deck';
    this.container.className = 'recent-work-deck-view';
    this.render();
  }

  getElement(): HTMLElement {
    return this.container;
  }

  /** Gathers and unifies all work logs in reverse chronological order */
  static getUnifiedWorkEntries(): WorkLogEntry[] {
    const entries: WorkLogEntry[] = [];

    // 1. Station Progression Sets
    const stationDefs: Array<{
      key: string;
      name: string;
      emblem: string;
      category: WorkLogCategory;
      route: string;
    }> = [
      { key: 'geometry-angles', name: 'Claw Protractor', emblem: '📐', category: 'geometry', route: '#/geometry' },
      { key: 'geometry-mirror', name: 'Benthic Mirror Line', emblem: '🪞', category: 'geometry', route: '#/geometry' },
      { key: 'fact-diver', name: 'Fact Diver', emblem: '🤿', category: 'comprehension', route: '#/fact-diver' },
      { key: 'comprehension', name: 'Clue Hunter', emblem: '📖', category: 'comprehension', route: '#/read' },
      { key: 'spelling', name: 'Spelling Reef', emblem: '🐚', category: 'spelling', route: '#/spelling' },
      { key: 'scan', name: 'Worksheet Remediation', emblem: '📷', category: 'scanner', route: '#/scan' },
    ];

    for (const def of stationDefs) {
      const prog = StationProgression.getProgression(def.key);
      const completed = Object.values(prog.completedSets) as SetCompletionRecord[];

      for (const rec of completed) {
        const percent = rec.maxScore > 0 ? Math.round((rec.score / rec.maxScore) * 100) : 100;
        entries.push({
          id: `prog_${def.key}_set_${rec.setNumber}_${rec.completedAt}`,
          timestamp: rec.completedAt,
          dateFormatted: new Date(rec.completedAt).toLocaleString('en-GB', {
            dateStyle: 'medium',
            timeStyle: 'short',
          }),
          category: def.category,
          stationName: def.name,
          stationEmblem: def.emblem,
          title: `${def.name} · Set ${rec.setNumber}`,
          subtitle: `Mastery Challenge Set ${rec.setNumber} completed`,
          scoreText: `${rec.score} / ${rec.maxScore} correct`,
          percent,
          stars: rec.stars,
          seal: rec.masterySeal,
          earnedEp: rec.stars === 3 ? 40 : 25,
          route: def.route,
        });
      }
    }

    // 2. Schoolwork Scans
    try {
      const scans: SchoolworkScanRecord[] = ScannerStorage.loadScans();
      for (const s of scans) {
        if (s.remediationResult) {
          const res = s.remediationResult;
          const percent = Math.round((res.correctCount / 5) * 100);
          entries.push({
            id: `scan_${s.id}`,
            timestamp: s.createdAt,
            dateFormatted: s.dateFormatted,
            category: 'scanner',
            stationName: 'Schoolwork Scanner',
            stationEmblem: '📷',
            title: `Worksheet: ${s.title}`,
            subtitle: `Remediated KS2 codes: ${s.diagnosedMisconceptions.map((m) => m.ks2Code).join(', ')}`,
            scoreText: `${res.correctCount} / 5 correct`,
            percent,
            stars: res.correctCount >= 5 ? 3 : res.correctCount >= 4 ? 2 : 1,
            seal: res.correctCount >= 4 ? 'silver' : 'bronze',
            earnedEp: res.earnedEp,
            route: '#/scan',
          });
        }
      }
    } catch {
      // Ignore mock storage failures
    }

    // 3. Mock Exams
    try {
      const mocks: MockExamRecord[] = MockExamStorage.loadRecords();
      for (const m of mocks) {
        const totalCorrect = m.paper1.correctCount + m.paper2.correctCount;
        const totalQuestions = m.paper1.totalQuestions + m.paper2.totalQuestions;
        const percent = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;
        entries.push({
          id: `mock_${m.id}`,
          timestamp: m.timestamp,
          dateFormatted: m.dateFormatted,
          category: 'mock',
          stationName: 'Full Mock Exam',
          stationEmblem: '🏛️',
          title: `Full Bexley Mock Trial (Composite SAS ${m.aggregateSas})`,
          subtitle: `Paper 1 (SAS ${m.paper1.sas}) & Paper 2 (SAS ${m.paper2.sas}) · ${m.isQualifying ? 'Qualifying Standard Reached' : 'Developing Stamina'}`,
          scoreText: `${totalCorrect} / ${totalQuestions} (${m.aggregateSas} SAS)`,
          percent,
          stars: m.isTop180 ? 3 : m.isQualifying ? 2 : 1,
          seal: m.isTop180 ? 'gold' : m.isQualifying ? 'silver' : 'bronze',
          earnedEp: 150,
          route: '#/mock',
        });
      }
    } catch {
      // Ignore
    }

    // 4. Spelling Reef Mastered Words
    try {
      const spellData = SpellingStorage.load();
      if (spellData.masteredWords.length > 0) {
        entries.push({
          id: `spelling_mastered_${spellData.masteredWords.length}`,
          timestamp: spellData.errorLog.length > 0 ? spellData.errorLog[spellData.errorLog.length - 1]!.timestamp : Date.now(),
          dateFormatted: spellData.lastSessionDate || 'Recent Session',
          category: 'spelling',
          stationName: 'Spelling Reef',
          stationEmblem: '🐚',
          title: `Spelling Masteries (${spellData.masteredWords.length} words)`,
          subtitle: `Mastered words: ${spellData.masteredWords.slice(0, 5).join(', ')}${spellData.masteredWords.length > 5 ? '…' : ''}`,
          scoreText: `${spellData.masteredWords.length} words`,
          percent: 100,
          stars: spellData.masteredWords.length >= 10 ? 3 : 2,
          seal: spellData.masteredWords.length >= 10 ? 'gold' : 'silver',
          earnedEp: spellData.masteredWords.length * 10,
          route: '#/spelling',
        });
      }
    } catch {
      // Ignore
    }

    // Sort descending by timestamp
    entries.sort((a, b) => b.timestamp - a.timestamp);
    return entries;
  }

  private render(): void {
    const allEntries = RecentWorkDeck.getUnifiedWorkEntries();
    const filtered = this.activeCategory === 'all'
      ? allEntries
      : allEntries.filter((e) => e.category === this.activeCategory);

    const totalMastered = allEntries.filter((e) => (e.stars || 0) >= 3 || e.seal === 'gold').length;
    const totalSessions = allEntries.length;
    const totalEpEarned = allEntries.reduce((sum, e) => sum + (e.earnedEp || 0), 0);

    const filterButtons: Array<{ id: WorkLogCategory; label: string; icon: string }> = [
      { id: 'all', label: `All Expeditions (${allEntries.length})`, icon: '📜' },
      { id: 'geometry', label: 'Geometry Sandbox', icon: '📐' },
      { id: 'comprehension', label: 'English & Retrieval', icon: '📖' },
      { id: 'spelling', label: 'Spelling Reef', icon: '🐚' },
      { id: 'scanner', label: 'Worksheet Scans', icon: '📷' },
      { id: 'mock', label: 'Bexley Mocks', icon: '🏛️' },
    ];

    this.container.innerHTML = `
      <div class="vellum-surface morris-card morris-card-gold" style="padding: 28px 24px; margin-bottom: 24px;">
        ${renderCornerFiligree()}
        <div style="position: relative; z-index: 2;">
          <!-- Header -->
          <div style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 16px; margin-bottom: 20px;">
            <div>
              <div class="atelier-eyebrow-pill" style="margin-bottom: 8px;">
                <span>📜</span> Parent &amp; Learner Logbook
              </div>
              <h2 style="font-family: ${tokens.font.heading}; font-size: 1.85rem; color: ${tokens.color.morrisIndigo}; margin: 0 0 6px;">
                Tudor's Recent Expeditions &amp; Work Archive
              </h2>
              <p style="font-size: 0.95rem; color: #3A4D66; max-width: 620px; margin: 0; line-height: 1.5;">
                Every challenge set Tudor completes, worksheet scanned, and mock paper finished is permanently charted here with scores, stars, and Victorian seals.
              </p>
            </div>

            <div style="display: flex; gap: 10px; flex-wrap: wrap;">
              <div style="text-align: center; padding: 10px 16px; background: rgba(255, 253, 249, 0.95); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 12px; box-shadow: 0 2px 8px rgba(10, 25, 47, 0.08);">
                <div style="font-size: 0.65rem; color: ${tokens.color.antiqueBrass}; font-weight: 800; text-transform: uppercase;">Completed Sets</div>
                <div style="font-family: ${tokens.font.mono}; font-size: 1.6rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">${totalSessions}</div>
              </div>
              <div style="text-align: center; padding: 10px 16px; background: rgba(255, 253, 249, 0.95); border: 1.5px solid ${tokens.color.seaweedSage}; border-radius: 12px; box-shadow: 0 2px 8px rgba(10, 25, 47, 0.08);">
                <div style="font-size: 0.65rem; color: ${tokens.color.seaweedSage}; font-weight: 800; text-transform: uppercase;">3-Star Masteries</div>
                <div style="font-family: ${tokens.font.mono}; font-size: 1.6rem; font-weight: 800; color: ${tokens.color.seaweedSage};">⭐ ${totalMastered}</div>
              </div>
              <div style="text-align: center; padding: 10px 16px; background: rgba(255, 246, 229, 0.95); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 12px; box-shadow: 0 2px 8px rgba(10, 25, 47, 0.08);">
                <div style="font-size: 0.65rem; color: ${tokens.color.gildedBrass}; font-weight: 800; text-transform: uppercase;">Total EP Earned</div>
                <div style="font-family: ${tokens.font.mono}; font-size: 1.6rem; font-weight: 800; color: ${tokens.color.morrisIndigo};">+${totalEpEarned}</div>
              </div>
            </div>
          </div>

          <!-- Category Filter Bar -->
          <div style="display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 20px;">
            ${filterButtons
              .map((b) => {
                const isActive = this.activeCategory === b.id;
                return `
                  <button
                    type="button"
                    class="touch-target work-filter-btn"
                    data-category="${b.id}"
                    style="
                      padding: 8px 16px;
                      border-radius: 20px;
                      font-size: 0.85rem;
                      font-weight: 700;
                      cursor: pointer;
                      transition: all 0.2s ease;
                      border: 1.5px solid ${isActive ? tokens.color.gildedBrass : 'rgba(26, 54, 93, 0.15)'};
                      background: ${isActive ? 'linear-gradient(135deg, #1A365D 0%, #0A192F 100%)' : 'rgba(255, 253, 249, 0.9)'};
                      color: ${isActive ? '#FFFDF9' : tokens.color.morrisIndigo};
                    "
                  >
                    <span>${b.icon}</span> ${b.label}
                  </button>
                `;
              })
              .join('')}
          </div>

          <!-- Entries Container -->
          <div style="display: flex; flex-direction: column; gap: 12px;">
            ${
              filtered.length === 0
                ? `
                <div style="text-align: center; padding: 36px 20px; background: rgba(255, 253, 249, 0.7); border: 1.5px dashed rgba(26, 54, 93, 0.2); border-radius: 14px;">
                  <div style="font-size: 2.2rem; margin-bottom: 8px;">🧭 📜</div>
                  <div style="font-family: ${tokens.font.heading}; font-size: 1.25rem; color: ${tokens.color.morrisIndigo}; margin-bottom: 6px;">
                    No expeditions logged yet for this category
                  </div>
                  <p style="font-size: 0.9rem; color: #5A6D86; max-width: 440px; margin: 0 auto 16px;">
                    When Tudor completes challenge sets, scans homework, or attempts mock papers, every result will be catalogued here.
                  </p>
                  <a href="#/" class="touch-target btn-primary" style="display: inline-flex; align-items: center; gap: 6px; padding: 10px 22px; text-decoration: none;">
                    🗺️ Open Expedition Map
                  </a>
                </div>
              `
                : filtered
                    .map((item) => {
                      const sealColor = item.seal === 'gold' ? '#D4AF37' : item.seal === 'silver' ? '#A0AEC0' : '#CD7F32';
                      const sealIcon = item.seal === 'gold' ? '🥇' : item.seal === 'silver' ? '🥈' : '🥉';
                      const starDisplay = item.stars ? '⭐'.repeat(item.stars) : '';

                      return `
                        <div
                          class="work-log-item-card"
                          style="
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            flex-wrap: wrap;
                            gap: 14px;
                            padding: 16px 20px;
                            background: rgba(255, 253, 249, 0.95);
                            border: 1.5px solid rgba(26, 54, 93, 0.15);
                            border-left: 5px solid ${sealColor};
                            border-radius: 12px;
                            box-shadow: 0 2px 6px rgba(10, 25, 47, 0.05);
                            transition: transform 0.15s ease, box-shadow 0.15s ease;
                          "
                        >
                          <div style="display: flex; align-items: center; gap: 14px; flex: 1; min-width: 260px;">
                            <div style="
                              width: 44px;
                              height: 44px;
                              border-radius: 10px;
                              background: rgba(26, 54, 93, 0.08);
                              border: 1px solid rgba(26, 54, 93, 0.15);
                              display: flex;
                              align-items: center;
                              justify-content: center;
                              font-size: 1.5rem;
                              flex-shrink: 0;
                            ">
                              ${item.stationEmblem}
                            </div>
                            <div>
                              <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
                                <span style="font-weight: 800; color: ${tokens.color.morrisIndigo}; font-size: 1.05rem;">
                                  ${item.title}
                                </span>
                                ${item.seal ? `
                                  <span style="
                                    font-size: 0.72rem;
                                    font-weight: 800;
                                    padding: 2px 8px;
                                    border-radius: 999px;
                                    background: rgba(255, 246, 229, 0.95);
                                    border: 1px solid ${sealColor};
                                    color: #4A3A16;
                                  ">
                                    ${sealIcon} ${item.seal.toUpperCase()} SEAL
                                  </span>
                                ` : ''}
                                ${starDisplay ? `
                                  <span style="font-size: 0.85rem;" title="${item.stars} Star Mastery">
                                    ${starDisplay}
                                  </span>
                                ` : ''}
                              </div>
                              <div style="font-size: 0.82rem; color: #5A6D86; margin-top: 2px;">
                                ${item.subtitle}
                              </div>
                              <div style="font-size: 0.72rem; color: #8C9BAE; margin-top: 2px;">
                                🕒 ${item.dateFormatted}
                              </div>
                            </div>
                          </div>

                          <div style="display: flex; align-items: center; gap: 14px;">
                            <div style="text-align: right;">
                              <div style="font-family: ${tokens.font.mono}; font-weight: 800; font-size: 1.15rem; color: ${tokens.color.morrisIndigo};">
                                ${item.scoreText}
                              </div>
                              <div style="font-size: 0.75rem; font-weight: 700; color: ${item.percent >= 80 ? tokens.color.seaweedSage : tokens.color.madderCoral};">
                                ${item.percent}% Accuracy
                              </div>
                            </div>
                            <a
                              href="${item.route}"
                              class="touch-target"
                              style="
                                padding: 8px 16px;
                                border-radius: 8px;
                                border: 1.5px solid ${tokens.color.morrisIndigo};
                                background: #FFFDF9;
                                color: ${tokens.color.morrisIndigo};
                                font-weight: 800;
                                font-size: 0.85rem;
                                text-decoration: none;
                                display: inline-flex;
                                align-items: center;
                                gap: 4px;
                              "
                            >
                              Replay &rarr;
                            </a>
                          </div>
                        </div>
                      `;
                    })
                    .join('')
            }
          </div>
        </div>
      </div>
    `;

    // Filter event listeners
    this.container.querySelectorAll<HTMLButtonElement>('.work-filter-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        soundEngine.playTap();
        const cat = btn.getAttribute('data-category') as WorkLogCategory;
        if (cat) {
          this.activeCategory = cat;
          this.render();
        }
      });
    });
  }
}
